import React, { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, Phone, Sparkles, Facebook, Twitter, Linkedin, Instagram, Globe, ChevronDown, Code, Smartphone, Building2, Layers3, Palette, Cloud, Wrench, Rocket, Brain, Zap, Users, Briefcase, Heart, TrendingUp, ChevronRight } from "lucide-react";

import { mock } from "@/mock";
import { cn } from "@/lib/utils";

import WorkshopPromotionalModal from "@/components/workshops/WorkshopPromotionalModal";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

function SocialLinks() {
  return (
    <div className="flex items-center gap-3 ml-4">
      <a href="https://facebook.com" target="_blank" rel="noopener" aria-label="Facebook">
        <Facebook className="h-5 w-5 text-white hover:text-primary transition" />
      </a>
      <a href="https://twitter.com" target="_blank" rel="noopener" aria-label="Twitter">
        <Twitter className="h-5 w-5 text-white hover:text-primary transition" />
      </a>
      <a href="https://linkedin.com" target="_blank" rel="noopener" aria-label="LinkedIn">
        <Linkedin className="h-5 w-5 text-white hover:text-primary transition" />
      </a>
      <a href="https://www.instagram.com/shiveda_it/" target="_blank" rel="noopener" aria-label="Instagram">
        <Instagram className="h-5 w-5 text-white hover:text-primary transition" />
      </a>
      <a href="https://shivedaitservices.com" target="_blank" rel="noopener" aria-label="Website">
        <Globe className="h-5 w-5 text-white hover:text-primary transition" />
      </a>
    </div>
  );
}

function useActiveHashScrollOffset() {
  // Allows /contact#form focusing to land nicely under the fixed header.
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;

    const headerOffset = 92;
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;

    // Wait a tick so layout settles.
    const t = window.setTimeout(() => {
      window.scrollTo({ top, behavior: "smooth" });
    }, 60);

    return () => window.clearTimeout(t);
  }, [pathname, hash]);
}

export function Reveal({ children, className, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "50px" },
    );

    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-[opacity,transform] duration-700 will-change-transform",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className,
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-16">
        <img 
          src="/Shiveda_Logo.png" 
          alt="Shiveda IT Services Logo" 
          className="w-full h-auto"
          onError={(e) => {
            console.error('Logo failed to load:', e);
            e.target.style.display = 'none';
          }}
        />
      </div>
        <div className="text-[14px] font-semibold text-white">
          Shiveda IT Services
        </div>
    </div>
  );
}

function DesktopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const servicesHoverTimer = useRef(null);
  const companyHoverTimer = useRef(null);
  
  const companyDropdownItems = [
    { 
      icon: Heart,
      label: "Culture", 
      to: "/culture",
      description: "Our values and work environment"
    },
    { 
      icon: Briefcase,
      label: "Career", 
      to: "/career",
      description: "Join our growing team"
    },
    { 
      icon: Users,
      label: "About Us", 
      to: "/about",
      description: "Our story and mission"
    }
  ];

  const servicesData = [
    {
      icon: Code,
      title: "Web Development",
      description: "Custom web applications and websites",
      to: "/services/web-development"
    },
    {
      icon: Smartphone,
      title: "Mobile App Development",
      description: "iOS and Android applications",
      to: "/services/mobile-development"
    },
    {
      icon: Building2,
      title: "ERP & Enterprise Services",
      description: "Enterprise resource planning solutions",
      to: "/services/erp-services"
    },
    {
      icon: Layers3,
      title: "SaaS Product Development",
      description: "Software as a Service platforms",
      to: "/services/saas-development"
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description: "User interface and experience design",
      to: "/services/uiux-design"
    },
    {
      icon: Cloud,
      title: "Cloud & DevOps",
      description: "Cloud infrastructure and automation",
      to: "/services/cloud-devops"
    },
    {
      icon: Wrench,
      title: "Maintenance & Support",
      description: "Ongoing support and maintenance",
      to: "/services/maintenance-support"
    },
    {
      icon: Rocket,
      title: "MVP & Rapid Prototyping",
      description: "Quick product validation and testing",
      to: "/services/mvp-rapid-prototyping"
    },
    {
      icon: Brain,
      title: "AI & Machine Learning Strategy",
      description: "Intelligent automation solutions",
      to: "/services/ai-ml-strategy"
    },
    {
      icon: Zap,
      title: "Generative AI Solutions",
      description: "Custom AI-powered applications",
      to: "/services/generative-ai-solutions"
    }
  ];

  return (
    <nav className="hidden items-center gap-6 lg:flex">
      {mock.nav.map((item) => {
        // Determine if current route is a services page
        const isServicesActive = item.label === "Services" && location.pathname.startsWith("/services");
        // Handle Company dropdown
        if (item.label === "Company") {
          return (
            <div 
              key={item.label} 
              className="relative"
              onMouseEnter={() => {
                clearTimeout(companyHoverTimer.current);
                setCompanyDropdownOpen(true);
              }}
              onMouseLeave={() => {
                companyHoverTimer.current = setTimeout(() => {
                  setCompanyDropdownOpen(false);
                }, 150);
              }}
            >
              <div className="flex items-center gap-1 select-none text-[14px] tracking-tight text-white/70 hover:text-white transition-colors cursor-default">
                <span>{item.label}</span>
                <ChevronDown className={cn(
                  "h-3 w-3 text-white/70 hover:text-white transition-all duration-300",
                  companyDropdownOpen && "rotate-180"
                )} />
              </div>
              
              {/* Mega Dropdown Container */}
              <div className={cn(
                "absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[70vw] max-w-[700px] transition-all duration-300 transform z-50",
                companyDropdownOpen 
                  ? "opacity-100 visible translate-y-0" 
                  : "opacity-0 invisible translate-y-2 pointer-events-none"
              )}
              onMouseEnter={() => {
                clearTimeout(companyHoverTimer.current);
                setCompanyDropdownOpen(true);
              }}
              onMouseLeave={() => {
                companyHoverTimer.current = setTimeout(() => {
                  setCompanyDropdownOpen(false);
                }, 150);
              }}>
                <div className="bg-black/98 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.6)] overflow-hidden">
                  {/* Header with Gradient */}
                  <div className="relative p-5 border-b border-white/10 bg-gradient-to-r from-primary/5 via-transparent to-primary/5">
                    <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="h-0.5 w-6 bg-primary rounded-full" />
                        <span className="text-[10px] font-medium text-primary uppercase tracking-wider">Who We Are</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">Discover Shiveda</h3>
                      <p className="text-xs text-white/60 max-w-2xl">Learn about our culture, values, and opportunities to grow with us</p>
                    </div>
                  </div>
                  
                  {/* Company Items Grid */}
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {companyDropdownItems.map((companyItem, idx) => {
                        const IconComponent = companyItem.icon;
                        const isActive = location.pathname === companyItem.to;
                        
                        return (
                          <NavLink
                            key={companyItem.to}
                            to={companyItem.to}
                            onClick={() => setCompanyDropdownOpen(false)}
                            style={{ animationDelay: `${idx * 30}ms` }}
                            className={cn(
                              "group/item relative p-3.5 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1",
                              "animate-in fade-in-50 slide-in-from-bottom-4",
                              isActive 
                                ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary/40 shadow-lg shadow-primary/20" 
                                : "bg-white/[0.03] border-white/10 hover:bg-gradient-to-br hover:from-primary/10 hover:to-transparent hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10"
                            )}
                          >
                            {/* Animated background gradient */}
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover/item:opacity-100 transition-all duration-500" />
                            
                            {/* Shine effect on hover */}
                            <div className="absolute inset-0 rounded-xl overflow-hidden">
                              <div className="absolute inset-0 translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                            </div>
                            
                            <div className="relative z-10 flex flex-col items-center text-center">
                              {/* Icon Container */}
                              <div className={cn(
                                "w-11 h-11 rounded-lg flex items-center justify-center mb-2.5 transition-all duration-300",
                                "shadow-lg",
                                isActive 
                                  ? "bg-gradient-to-br from-primary/30 to-primary/10 text-primary shadow-primary/30" 
                                  : "bg-gradient-to-br from-white/10 to-white/5 text-white/70 group-hover/item:from-primary/30 group-hover/item:to-primary/10 group-hover/item:text-primary group-hover/item:shadow-primary/30 group-hover/item:scale-110"
                              )}>
                                <IconComponent className="w-5 h-5" />
                              </div>
                              
                              {/* Title */}
                              <h4 className={cn(
                                "font-semibold text-xs mb-1.5 transition-colors duration-300 leading-tight",
                                isActive 
                                  ? "text-primary" 
                                  : "text-white group-hover/item:text-primary"
                              )}>
                                {companyItem.label}
                              </h4>
                              
                              {/* Description */}
                              <p className={cn(
                                "text-[10px] leading-relaxed transition-colors duration-300",
                                isActive
                                  ? "text-white/70"
                                  : "text-white/50 group-hover/item:text-white/70"
                              )}>
                                {companyItem.description}
                              </p>
                              
                              {/* Active indicator */}
                              {isActive && (
                                <div className="mt-2 h-0.5 w-5 bg-primary rounded-full" />
                              )}
                            </div>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Enhanced Footer CTA */}
                  <div className="relative p-4 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 border-t border-white/10">
                    <div className="absolute inset-0 bg-white/[0.01]" />
                    <div className="relative flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="text-center sm:text-left">
                        <p className="text-xs font-semibold text-white mb-0.5 flex items-center gap-2 justify-center sm:justify-start">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                          Want to join our team?
                        </p>
                        <p className="text-[10px] text-white/60">Explore exciting career opportunities and grow with Shiveda</p>
                      </div>
                      <button 
                        onClick={() => {
                          setCompanyDropdownOpen(false);
                          navigate("/career");
                        }}
                        className="group flex items-center gap-2 px-5 py-2.5 bg-primary text-black text-xs font-semibold rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:scale-105"
                      >
                        View Openings
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Handle Services mega dropdown
        if (item.label === "Services") {
          return (
            <div 
              key={item.label} 
              className="relative"
              onMouseEnter={() => {
                clearTimeout(servicesHoverTimer.current);
                setServicesDropdownOpen(true);
              }}
              onMouseLeave={() => {
                servicesHoverTimer.current = setTimeout(() => {
                  setServicesDropdownOpen(false);
                }, 150);
              }}
            >
              <div className={cn(
                "flex items-center gap-1 select-none text-[14px] tracking-tight hover:text-white transition-colors cursor-default",
                isServicesActive ? "text-primary" : "text-white/70"
              )}>
                <span>{item.label}</span>
                <ChevronDown className={cn(
                  "h-3 w-3 hover:text-white transition-all duration-300",
                  isServicesActive ? "text-primary" : "text-white/70",
                  servicesDropdownOpen && "rotate-180"
                )} />
              </div>
              
              {/* Mega Dropdown Container */}
              <div className={cn(
                "absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[85vw] max-w-[1000px] transition-all duration-300 transform z-50",
                servicesDropdownOpen 
                  ? "opacity-100 visible translate-y-0" 
                  : "opacity-0 invisible translate-y-2 pointer-events-none"
              )}
              onMouseEnter={() => {
                clearTimeout(servicesHoverTimer.current);
                setServicesDropdownOpen(true);
              }}
              onMouseLeave={() => {
                servicesHoverTimer.current = setTimeout(() => {
                  setServicesDropdownOpen(false);
                }, 150);
              }}>
                <div className="bg-black/98 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.6)] overflow-hidden">
                  {/* Header with Gradient */}
                  <div className="relative p-5 border-b border-white/10 bg-gradient-to-r from-primary/5 via-transparent to-primary/5">
                    <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="h-0.5 w-6 bg-primary rounded-full" />
                        <span className="text-[10px] font-medium text-primary uppercase tracking-wider">Explore Services</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">What We Offer</h3>
                      <p className="text-xs text-white/60 max-w-2xl">End-to-end IT solutions designed to help your business grow and scale efficiently</p>
                    </div>
                  </div>
                  
                  {/* Services Grid */}
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {servicesData.map((service, idx) => {
                        const IconComponent = service.icon;
                        const isActive = location.pathname === service.to;
                        
                        return (
                          <NavLink
                            key={service.to}
                            to={service.to}
                            onClick={() => setServicesDropdownOpen(false)}
                            style={{ animationDelay: `${idx * 30}ms` }}
                            className={cn(
                              "group/item relative p-3.5 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1",
                              "animate-in fade-in-50 slide-in-from-bottom-4",
                              isActive 
                                ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary/40 shadow-lg shadow-primary/20" 
                                : "bg-white/[0.03] border-white/10 hover:bg-gradient-to-br hover:from-primary/10 hover:to-transparent hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10"
                            )}
                          >
                            {/* Animated background gradient */}
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover/item:opacity-100 transition-all duration-500" />
                            
                            {/* Shine effect on hover */}
                            <div className="absolute inset-0 rounded-xl overflow-hidden">
                              <div className="absolute inset-0 translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                            </div>
                            
                            <div className="relative z-10 flex flex-col items-center text-center">
                              {/* Icon Container */}
                              <div className={cn(
                                "w-11 h-11 rounded-lg flex items-center justify-center mb-2.5 transition-all duration-300",
                                "shadow-lg",
                                isActive 
                                  ? "bg-gradient-to-br from-primary/30 to-primary/10 text-primary shadow-primary/30" 
                                  : "bg-gradient-to-br from-white/10 to-white/5 text-white/70 group-hover/item:from-primary/30 group-hover/item:to-primary/10 group-hover/item:text-primary group-hover/item:shadow-primary/30 group-hover/item:scale-110"
                              )}>
                                <IconComponent className="w-5 h-5" />
                              </div>
                              
                              {/* Title */}
                              <h4 className={cn(
                                "font-semibold text-xs mb-1.5 transition-colors duration-300 leading-tight",
                                isActive 
                                  ? "text-primary" 
                                  : "text-white group-hover/item:text-primary"
                              )}>
                                {service.title}
                              </h4>
                              
                              {/* Description */}
                              <p className={cn(
                                "text-[10px] leading-relaxed transition-colors duration-300",
                                isActive
                                  ? "text-white/70"
                                  : "text-white/50 group-hover/item:text-white/70"
                              )}>
                                {service.description}
                              </p>
                              
                              {/* Active indicator */}
                              {isActive && (
                                <div className="mt-2 h-0.5 w-5 bg-primary rounded-full" />
                              )}
                            </div>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Enhanced Footer CTA */}
                  <div className="relative p-4 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 border-t border-white/10">
                    <div className="absolute inset-0 bg-white/[0.01]" />
                    <div className="relative flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="text-center sm:text-left">
                        <p className="text-xs font-semibold text-white mb-0.5 flex items-center gap-2 justify-center sm:justify-start">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                          Need a custom solution?
                        </p>
                        <p className="text-[10px] text-white/60">Let's discuss your unique requirements and find the perfect fit</p>
                      </div>
                      <button 
                        onClick={() => {
                          setServicesDropdownOpen(false);
                          window.open("https://cal.com/vadri-2cqnbp", "_blank");
                        }}
                        className="group flex items-center gap-2 px-5 py-2.5 bg-primary text-black text-xs font-semibold rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:scale-105"
                      >
                        Schedule a Call
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Regular nav items
        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "text-[14px] tracking-tight transition-[color] duration-200",
                isActive ? "text-primary" : "text-white/70 hover:text-white",
              )
            }
          >
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function MobileNav() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);

  const servicesData = [
    { icon: Code, label: "Web Development", to: "/services/web-development" },
    { icon: Smartphone, label: "Mobile App Development", to: "/services/mobile-development" },
    { icon: Building2, label: "ERP & Enterprise", to: "/services/erp-services" },
    { icon: Layers3, label: "SaaS Development", to: "/services/saas-development" },
    { icon: Palette, label: "UI/UX Design", to: "/services/uiux-design" },
    { icon: Cloud, label: "Cloud & DevOps", to: "/services/cloud-devops" },
    { icon: Wrench, label: "Maintenance & Support", to: "/services/maintenance-support" },
    { icon: Rocket, label: "MVP & Rapid Prototyping", to: "/services/mvp-rapid-prototyping" },
    { icon: Brain, label: "AI & ML Strategy", to: "/services/ai-ml-strategy" },
    { icon: Zap, label: "Generative AI Solutions", to: "/services/generative-ai-solutions" }
  ];

  const companyItems = [
    { icon: Heart, label: "Culture", to: "/culture" },
    { icon: Briefcase, label: "Career", to: "/career" },
    { icon: Users, label: "About Us", to: "/about" }
  ];

  const location = useLocation();
  const mainNavItems = mock.nav.filter(item => 
    item.label !== "Services" && item.label !== "Company"
  );
  const isServicesActive = location.pathname.startsWith("/services");

  React.useEffect(() => {
    let original = '';
    if (open && window.innerWidth < 1024) {
      original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    return () => {
      // Always restore scroll on close/unmount
      document.body.style.overflow = original || '';
    };
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="secondary"
          className="lg:hidden rounded-xl bg-white/[0.06] border border-white/15 text-white hover:bg-white/10 hover:text-white"
        >
          <Menu className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-black border-white/10 flex flex-col h-[100vh] overflow-y-auto lg:hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="text-white">Navigate</SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-2 flex-1 pr-2">
          {/* Main Nav Items */}
          {mainNavItems.map((item) => (
            <button
              key={item.to}
              type="button"
              onClick={() => {
                navigate(item.to);
                setOpen(false);
              }}
              className="w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-left transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20 text-white"
            >
              {item.label}
            </button>
          ))}

          {/* Services Collapsible */}
          <Collapsible open={servicesOpen} onOpenChange={setServicesOpen}>
            <CollapsibleTrigger className={cn(
              "w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-left transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20 flex items-center justify-between",
              isServicesActive ? "text-primary" : "text-white"
            )}>
              <span className="font-medium">Services</span>
              <ChevronRight className={cn(
                "h-4 w-4 transition-transform duration-200",
                isServicesActive ? "text-primary" : "",
                servicesOpen && "rotate-90"
              )} />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1.5 pl-4 max-h-[60vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              {servicesData.map((service) => {
                const Icon = service.icon;
                return (
                  <button
                    key={service.to}
                    type="button"
                    onClick={() => {
                      navigate(service.to);
                      setOpen(false);
                    }}
                    className="w-full rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5 text-left transition-all duration-200 hover:bg-white/[0.06] hover:border-white/10 flex items-center gap-3 group"
                  >
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm text-white/80 group-hover:text-white">{service.label}</span>
                  </button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>

          {/* Company Collapsible */}
          <Collapsible open={companyOpen} onOpenChange={setCompanyOpen}>
            <CollapsibleTrigger className="w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-left transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20 flex items-center justify-between text-white">
              <span className="font-medium">Company</span>
              <ChevronRight className={cn(
                "h-4 w-4 transition-transform duration-200",
                companyOpen && "rotate-90"
              )} />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1.5 pl-4">
              {companyItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.to}
                    type="button"
                    onClick={() => {
                      navigate(item.to);
                      setOpen(false);
                    }}
                    className="w-full rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5 text-left transition-all duration-200 hover:bg-white/[0.06] hover:border-white/10 flex items-center gap-3 group"
                  >
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm text-white/80 group-hover:text-white">{item.label}</span>
                  </button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div className="flex-shrink-0 mt-auto pt-4 border-t border-white/10">
          <div className="text-xs text-white/55 mb-3">Quick connect</div>
          <Button
            onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
            className="w-full rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
          >
            <Phone className="mr-2 h-4 w-4" />
            Get a Quote
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="relative">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-2xl" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        <div className="relative flex h-[84px] w-full items-center justify-between px-5 xl:px-10 2xl:px-14">
          <button
            type="button"
            onClick={() => {
              if (location.pathname === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            className="transition-[transform] duration-200 hover:translate-y-[-1px]"
          >
            <BrandMark />
          </button>

          <DesktopNav />

          <div className="flex items-center gap-3">
            <Button
              //onClick={() => navigate("/contact#form")}
              onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
              className={cn(
                "hidden rounded-xl bg-primary text-black lg:inline-flex btn-glow",
                "transition-[transform,box-shadow,background-color] duration-200",
                "hover:bg-primary/90 hover:-translate-y-[1px]",
              )}
            >
              Get a Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            <MobileNav />
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const navigate = useNavigate();
  
  const servicesLinks = [
    { label: "Web Development", to: "/services/web-development" },
    { label: "Mobile App Development", to: "/services/mobile-development" },
    { label: "SaaS Development", to: "/services/saas-development" },
    { label: "UI/UX Design", to: "/services/uiux-design" },
    { label: "Cloud & DevOps", to: "/services/cloud-devops" },
    { label: "AI & ML Strategy", to: "/services/ai-ml-strategy" },
    { label: "Generative AI Solutions", to: "/services/generative-ai-solutions" },
    { label: "ERP Services", to: "/services/erp-services" },
    { label: "MVP & Rapid Prototyping", to: "/services/mvp-rapid-prototyping" },
    { label: "Maintenance & Support", to: "/services/maintenance-support" }
  ];

  const companyLinks = [
    { label: "About Us", to: "/about" },
    { label: "Culture", to: "/culture" },
    { label: "Career", to: "/career" },
    { label: "Projects", to: "/projects" },
    { label: "Workshops", to: "/workshops" },
    { label: "Pricing", to: "/pricing" }
  ];

  return (
    <footer className="mt-24 border-t border-white/10 bg-black">
      <div className="w-full px-5 py-14 xl:px-10 2xl:px-14 max-w-[1400px] mx-auto">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand Section */}
          <div className="lg:col-span-4">
            <div className="flex items-center">
              <BrandMark />
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
              {mock.company.shortBlurb}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs text-white/70">
                Performance-first builds
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs text-white/70">
                Clean architecture
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs text-white/70">
                Reliable delivery
              </span>
            </div>
            
            {/* Social Links */}
            <div className="mt-6">
              <div className="text-sm font-medium text-white mb-3">Follow Us</div>
              <div className="flex items-center gap-3">
                <a href="https://facebook.com" target="_blank" rel="noopener" aria-label="Facebook" className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Facebook className="h-4 w-4 text-white" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener" aria-label="Twitter" className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Twitter className="h-4 w-4 text-white" />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener" aria-label="LinkedIn" className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Linkedin className="h-4 w-4 text-white" />
                </a>
                <a href="https://www.instagram.com/shiveda_it/" target="_blank" rel="noopener" aria-label="Instagram" className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Instagram className="h-4 w-4 text-white" />
                </a>
                <a href="https://shivedaitservices.com" target="_blank" rel="noopener" aria-label="Website" className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <Globe className="h-4 w-4 text-white" />
                </a>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="lg:col-span-3">
            <div className="text-sm font-semibold text-white mb-4">Services</div>
            <div className="grid gap-2.5">
              {servicesLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className="text-sm text-white/60 transition-colors duration-200 hover:text-primary"
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Company Section */}
          <div className="lg:col-span-2">
            <div className="text-sm font-semibold text-white mb-4">Company</div>
            <div className="grid gap-2.5">
              {companyLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className="text-sm text-white/60 transition-colors duration-200 hover:text-primary"
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="lg:col-span-3">
            <div className="text-sm font-semibold text-white mb-4">Contact Us</div>
            <div className="space-y-3 text-sm text-white/60">
              <div className="flex items-start gap-2">
                <Globe className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
                <span>{mock.company.contact.location}</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a
                  className="transition-colors duration-200 hover:text-primary"
                  href={`mailto:${mock.company.contact.email}`}
                >
                  {mock.company.contact.email}
                </a>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
                <span>{mock.company.contact.whatsapp}</span>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={() => navigate("/contact")}
                className="w-full rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow"
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Start a Project
              </Button>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-white/10" />

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="text-xs text-white/45">
            © {new Date().getFullYear()} {mock.company.name}. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/45">
            <NavLink to="/legal" className="hover:text-white/70 transition-colors duration-200">
              Privacy Policy
            </NavLink>
            <NavLink to="/legal" className="hover:text-white/70 transition-colors duration-200">
              Terms of Service
            </NavLink>
            <NavLink to="/contact" className="hover:text-white/70 transition-colors duration-200">
              Contact
            </NavLink>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const location = useLocation();
  useActiveHashScrollOffset();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Background grid + subtle noise */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.12] [background-image:repeating-linear-gradient(0deg,transparent,transparent_7.5%,rgba(255,255,255,0.75)_calc(7.5%+1px)),repeating-linear-gradient(90deg,transparent,transparent_7.5%,rgba(255,255,255,0.75)_calc(7.5%+1px))]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_20%_-10%,rgba(0,194,255,0.14),transparent_60%),radial-gradient(900px_520px_at_90%_15%,rgba(255,255,255,0.06),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.06] [background-image:url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.7%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22 opacity=%220.55%22/%3E%3C/svg%3E')]" />
      </div>

      <main className="pt-[92px]">
        <Outlet />
      </main>

      <Footer />

      {/* Global Workshop Promotional Modal */}
      <WorkshopPromotionalModal />
    </div>
  );
}
